/**
 * Parse desk-assist SSE text into enrichment-aligned keys (draft_body / operator_facts).
 * Mirrors Fabrico\Ticket\DTO\AssistDraftOutput::parseModelText.
 *
 * @param {string} modelText
 * @returns {{ draftBody: string, operatorFacts: string|null, wasStructured: boolean }}
 */
export function parseAssistDraftOutput(modelText) {
    const trimmed = String(modelText || '').trim();
    if (!trimmed) {
        return { draftBody: '', operatorFacts: null, wasStructured: false };
    }

    const candidate = extractJsonCandidate(trimmed);
    if (candidate) {
        try {
            const decoded = JSON.parse(candidate);
            if (decoded && typeof decoded === 'object' && Object.prototype.hasOwnProperty.call(decoded, 'draft_body')) {
                const draftBody = stripLeadingSubjectHeader(String(decoded.draft_body ?? '').trim());
                let operatorFacts = Object.prototype.hasOwnProperty.call(decoded, 'operator_facts')
                    && decoded.operator_facts !== null
                    && decoded.operator_facts !== undefined
                    ? String(decoded.operator_facts).trim()
                    : null;
                if (operatorFacts === '') {
                    operatorFacts = null;
                }
                return { draftBody, operatorFacts, wasStructured: true };
            }
        } catch (_e) {
            // fall through to prose fallback
        }
    }

    return {
        draftBody: stripLeadingSubjectHeader(trimmed),
        operatorFacts: null,
        wasStructured: false,
    };
}

/**
 * Drop accidental email subject lines (e.g. `**Oggetto:** Re: …`).
 * Mirrors AssistDraftOutput::stripLeadingSubjectHeader.
 *
 * @param {string} draftBody
 * @returns {string}
 */
export function stripLeadingSubjectHeader(draftBody) {
    const lines = String(draftBody || '').split(/\r\n|\n|\r/);
    let i = 0;
    while (i < lines.length) {
        if (/^\*{0,2}\s*(Oggetto|Subject)\s*\*{0,2}\s*:.*$/iu.test(lines[i])) {
            i += 1;
            if (i < lines.length && lines[i].trim() === '') {
                i += 1;
            }
            continue;
        }
        break;
    }
    if (i === 0) {
        return draftBody;
    }
    return lines.slice(i).join('\n').trim();
}

/**
 * @param {string} text
 * @returns {string|null}
 */
function extractJsonCandidate(text) {
    const fenced = text.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (fenced && fenced[1]) {
        return fenced[1].trim();
    }

    if (text.startsWith('{') && text.endsWith('}')) {
        return text;
    }

    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start !== -1 && end > start) {
        return text.slice(start, end + 1);
    }

    return null;
}
